import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { FormattedMessage, useIntl } from 'react-intl';

import {
    CalculatorStepAccordion,
    CalculationPanel,
    CalculationLineBlock,
    Question,
    FormComputed,
    Sizes,
    Colours,
    useForm,
    MessagePropType,
    useCalculator,
    BOOLEAN_OPTIONS,
    MessageBanner,
} from '@ratehub/base-ui';
import { MORTGAGE_PLACEHOLDERS, GREATER_THAN_OR_EQUAL_TO_ZERO_VALIDATOR } from '@ratehub/mtg-common';


function RefinanceStepNewMortgage({
    title,
    ctaText,
    ctaDataName,
    stepNumber,
    onToggleExpand,
    onConfirmComplete,
    ...otherProps
}) {
    const form = useForm();
    const intl = useIntl();

    const calculatorState = useCalculator();
    const document = calculatorState.document;

    const {
        propertyValue,
        mortgageBalance,
        additionalEquity,
    } = getSavedFormValues(toJS(document?.journey?.calculators));

    return (
        <Container {...otherProps}>
            <FormComputed
                name="newMortgageAmount"
                value={calculateMortgageAmount}
            />

            <CalculatorStepAccordion
                title={title}
                ctaText={ctaText}
                ctaDataName={ctaDataName}
                value="newMortgageAmount"
                valueFormat="CAD0"
                stepNumber={stepNumber}
                showBadge={true}
                isInitiallyExpanded={true}
                hasCompleteButton={true}
                onToggleExpand={onToggleExpand}
                onConfirmComplete={(finishedStep, formValues) => onConfirmComplete?.({ stepNumber: finishedStep, formValues, document })}
            >
                <div className="stepContent">
                    <Question
                        field={{
                            name: 'propertyValue',
                            type: 'integer',
                            validators: [
                                GREATER_THAN_OR_EQUAL_TO_ZERO_VALIDATOR,
                            ],
                            value: propertyValue,
                        }}
                        type="currency"
                        labelMessage={(
                            <FormattedMessage
                                id="mtg-calculator.Refinance.NewMortgage.propertyValueLabel"
                                defaultMessage="Current value of your property"
                            />
                        )}
                        placeholder={intl.formatMessage(MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER)}
                        isSideBySide={true}
                    />

                    <Question
                        field={{
                            name: 'mortgageBalance',
                            type: 'integer',
                            validators: [
                                {
                                    fn: (balance, values) => balance <= values.propertyValue,
                                    message: (
                                        <FormattedMessage
                                            id="mtg-calculator.Refinance.NewMortgage.mortgageBalanceErrorMessage"
                                            defaultMessage="Please enter a value less than the current value of your property."
                                        />
                                    ),
                                },
                            ],
                            value: mortgageBalance,
                        }}
                        type="currency"
                        labelMessage={(
                            <FormattedMessage
                                id="mtg-calculator.Refinance.NewMortgage.mortgageBalanceLabel"
                                defaultMessage="Your current mortgage balance"
                            />
                        )}
                        placeholder={intl.formatMessage(MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER)}
                        isSideBySide={true}
                    />

                    <If condition={form.values.propertyValue && form.values.mortgageBalance}>
                        <CalculationPanel
                            className="calculationPanel"
                            title={(
                                <FormattedMessage
                                    id="mtg-calculator.Refinance.NewMortgage.equityPanelTitle"
                                    defaultMessage="Determining the equity in your home"
                                />
                            )}
                        >
                            <CalculationLineBlock formulas={AVAILABLE_EQUITY_FORMULA} />
                        </CalculationPanel>
                    </If>

                    <If condition={form.values.availableEquity === 0}>
                        <MessageBanner
                            iconKey="exclamation-mark"
                            message={(
                                <FormattedMessage
                                    id="mtg-calculator.Refinance.NewMortgage.availableEquityError"
                                    defaultMessage="In Canada, the maximum equity you can access at refinance cannot be more than 80% of your home's value. Since your current mortgage balance is greater than (or equal to) 80% of your home's value, you cannot access any cash (equity) from your home at this time."
                                />
                            )}
                        />
                    </If>

                    <If condition={form.values.availableEquity > 0}>
                        <Question
                            field={{
                                name: 'requiresAdditionalEquity',
                                type: 'boolean',
                                value: !!additionalEquity,
                                options: BOOLEAN_OPTIONS,
                            }}
                            type="radio"
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculator.Refinance.NewMortgage.additionalEquityChoiceLabel"
                                    defaultMessage="Do you want to increase your mortgage to access the available cash?"
                                />
                            )}
                            flowDirection="row"
                            isSideBySide={true}
                            tooltipMessage={(
                                <FormattedMessage
                                    id="mtg-calculator.Refinance.NewMortgage.additionalEquityTooltip"
                                    defaultMessage="When you refinance your mortgage, you may have the option to access a portion of the equity you’ve accumulated in your home. You will receive this equity in cash when the refinance process is completed. "
                                />
                            )}
                        />

                        <Question
                            field={{
                                name: 'additionalEquity',
                                type: 'integer',
                                required: false,
                                validators: [
                                    {
                                        fn: (requestedEquity, { availableEquity }) => requestedEquity <= availableEquity,
                                        message: (
                                            <FormattedMessage
                                                id="mtg-calculator.Refinance.NewMortgage.additionalEquityError"
                                                defaultMessage="You cannot access more equity than is available"
                                            />
                                        ),
                                    },
                                ],
                                value: additionalEquity,
                            }}
                            type="currency"
                            isVisible={form?.values?.requiresAdditionalEquity ?? false}
                            labelMessage={(
                                <FormattedMessage
                                    id="mtg-calculator.Refinance.NewMortgage.additionalEquityLabel"
                                    defaultMessage="How much cash do you want to access up to {availableEquity, number, CAD0}?"
                                    values={{ availableEquity: form.values.availableEquity }}
                                />
                            )}
                            placeholder={intl.formatMessage(MORTGAGE_PLACEHOLDERS.AMOUNT_PLACEHOLDER)}
                            showOptionalText={false}
                            isSideBySide={true}
                        />
                    </If>

                    <If condition={form.values.newMortgageAmount && form.values.requiresAdditionalEquity}>
                        <CalculationPanel
                            className="calculationPanel"
                            title={(
                                <FormattedMessage
                                    id="mtg-calculator.Refinance.NewMortgage.howWeCalculatePanelTitle"
                                    defaultMessage="How we calculate this"
                                />
                            )}
                        >
                            <CalculationLineBlock formulas={NEW_MORTGAGE_FORMULA} />
                        </CalculationPanel>
                    </If>
                </div>
            </CalculatorStepAccordion>
        </Container>
    );
}

const AVAILABLE_EQUITY_FORMULA = [
    {
        id: 'propertyValue',
        name: 'propertyValue',
        label: (
            <FormattedMessage
                id="mtg-calculator.Refinance.NewMortgage.propertyValue"
                defaultMessage="Current home value"
            />
        ),
        operandValue: 'propertyValue',
    },
    {
        id: 'maxLTV',
        name: 'maxLTV',
        label: (
            <FormattedMessage
                id="mtg-calculator.Refinance.NewMortgage.maxLTV"
                defaultMessage="Maximum mortgage percentage"
            />
        ),
        operandValue: 0.8,
        operandFormat: 'percent0',
        operator: 'times',
    },
    {
        id: 'maxLoanAmount',
        name: 'maxLoanAmount',
        label: (
            <FormattedMessage
                id="mtg-calculator.Refinance.NewMortgage.maxLoanAmount"
                defaultMessage="Maximum mortgage amount"
            />
        ),
        operandValue: ({ propertyValue }) => propertyValue ? propertyValue * 0.8 : 0,
        operator: 'equals',
    },
    {
        id: 'mortgageBalance',
        name: 'mortgageBalance',
        label: (
            <FormattedMessage
                id="mtg-calculator.Refinance.NewMortgage.mortgageBalance"
                defaultMessage="Current mortgage balance"
            />
        ),
        operandValue: 'mortgageBalance',
        operator: 'minus',
    },
    {
        id: 'availableEquity',
        name: 'availableEquity',
        label: (
            <FormattedMessage
                id="mtg-calculator.Refinance.NewMortgage.availableEquity"
                defaultMessage="Cash available to access"
            />
        ),
        operandValue: ({ mortgageBalance, maxLoanAmount }) => {
            if (mortgageBalance && maxLoanAmount) {
                // mortgageBalance might exceed maxLoanAmount but we don't want to show a negative number
                return Math.max(maxLoanAmount - mortgageBalance, 0);
            }

            return 0;
        },
        operator: 'equals',
        result: true,
    },
];

const NEW_MORTGAGE_FORMULA = [
    {
        id: 'mortgageBalance',
        name: 'mortgageBalance',
        label: (
            <FormattedMessage
                id="mtg-calculator.Refinance.NewMortgage.currentMortgageBalance"
                defaultMessage="Current mortgage"
            />
        ),
        operandValue: 'mortgageBalance',
    },
    {
        id: 'requestedEquity',
        name: 'requestedEquity',
        label: (
            <FormattedMessage
                id="mtg-calculator.Refinance.NewMortgage.additionalEquity"
                defaultMessage="Additional equity"
            />
        ),
        operandValue: ({ additionalEquity }) => additionalEquity ?? 0,
        operator: 'plus',
    },
    {
        id: 'newMortgageAmount',
        name: 'newMortgageAmount',
        label: (
            <FormattedMessage
                id="mtg-calculator.Refinance.NewMortgage.newMortgageAmount"
                defaultMessage="New mortgage amount"
            />
        ),
        operandValue: 'newMortgageAmount',
        operator: 'equals',
        result: true,
    },
];

RefinanceStepNewMortgage.propTypes = {
    title: MessagePropType.isRequired,
    ctaText: MessagePropType.isRequired,
    ctaDataName: PropTypes.string.isRequired,
    stepNumber: PropTypes.number.isRequired,

    onConfirmComplete: PropTypes.func,
    onToggleExpand: PropTypes.func,
};

RefinanceStepNewMortgage.defaultProps = {
    onConfirmComplete: undefined,
    onToggleExpand: undefined,
};

function calculateMortgageAmount({ mortgageBalance, additionalEquity }) {
    return (mortgageBalance ?? 0) + (additionalEquity ?? 0);
}

function getSavedFormValues(savedCalculatorData) {
    const property = savedCalculatorData?.property;

    return {
        propertyValue: property?.homeValue,
        mortgageBalance: property?.existingMortgage?.currentMortgageBalance,
        additionalEquity: savedCalculatorData?.mortgage?.additionalFundsRequired,
    };
}

const Container = styled.section`
    .stepContent {
        padding: ${Sizes.SPACING.ONE} 0 0;

        > .calculationPanel {
            margin-bottom: ${Sizes.SPACING.THREE};
        }

        > .resultBox {
            border-radius: 6px;

            padding: ${Sizes.SPACING.ONE};
            margin: ${Sizes.SPACING.THREE} 0 ${Sizes.SPACING.HALF};
            background-color: ${Colours.BLUEBERRY_DARK};

            > .label,
            > .result {
                color: ${Colours.COCONUT};
                margin: 0;
            }

            > .result {
                margin-top: ${Sizes.SPACING.HALF};
                font-size: ${Sizes.FONTS['3XL']};
            }

            &[data-disabled] {
                opacity: 0.4;
            }
        }
    }
`;

export default observer(RefinanceStepNewMortgage);
